import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container, Typography, Card, CardContent, TextField, Button, Box,
  Alert, CircularProgress, Divider, Chip, Stack, Skeleton
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const BILLING_API = process.env.REACT_APP_BILLING_API_URL || 'http://localhost:5020';

export default function MockCheckoutPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const isTrial = searchParams.get('trial') === 'true';
  const subscriptionId = searchParams.get('subscription_id');

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [cardNumber, setCardNumber] = useState('4242424242424242');

  useEffect(() => {
    // Trial card collection was auto-completed by mock provider
    if (isTrial && !sessionId) {
      setSuccess(true);
      setLoading(false);
      return;
    }
    if (!sessionId) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }
    loadSession();
  }, [sessionId, isTrial]);

  async function loadSession() {
    try {
      const res = await fetch(`${BILLING_API}/api/mock/session/${sessionId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Session not found');
      setSession(data.session);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handlePay() {
    setProcessing(true);
    setError('');

    try {
      const res = await fetch(`${BILLING_API}/api/mock/complete-checkout/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardNumber: cardNumber.replace(/\s/g, '') })
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Payment failed');
      }

      // Redirect to success URL
      const successUrl = session.success_url?.replace('{CHECKOUT_SESSION_ID}', sessionId);
      if (successUrl) {
        window.location.href = successUrl;
      }
    } catch (err) {
      setError(err.message);
      setProcessing(false);
    }
  }

  if (loading) {
    // 999.2151: skeleton order summary per brand Loading & Busy-State Standard
    // (rule 7 — mock/dev surfaces not exempt); full-page spinner deprecated.
    return (
      <Container maxWidth="sm" sx={{ py: 8 }} aria-busy="true">
        <Skeleton variant="text" width="50%" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={72} sx={{ mb: 1.5, borderRadius: '2px' }} />
        <Skeleton variant="rectangular" height={72} sx={{ mb: 1.5, borderRadius: '2px' }} />
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: '2px' }} />
        <Box role="status" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Loading checkout…</Box>
      </Container>
    );
  }

  if (error && !session) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Alert severity="success" sx={{ mb: 3 }}>
          {isTrial ? 'Card saved! Your free trial has started.' : 'Payment successful!'}
        </Alert>
        <Button variant="contained" href="/subscriptions">
          View Subscriptions
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <LockIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
        <Typography variant="h5" sx={{ mt: 1 }}>
          {isTrial ? 'Start Free Trial' : 'Mock Checkout'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isTrial
            ? 'Add a card to start your trial — you won\'t be charged until it ends'
            : 'Development mode — no real charges'}
        </Typography>
      </Box>

      <Card>
        <CardContent>
          {session && (() => {
            const meta = session.metadata || {};
            const price = meta.price_cents ? (parseInt(meta.price_cents) / 100).toFixed(2) : null;
            const interval = meta.interval_type === 'year' ? 'year' : 'month';
            const trialDays = session.subscription_data?.trial_period_days;
            return (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">Plan</Typography>
                <Typography variant="h5">
                  {meta.plan_name || 'Subscription'}
                </Typography>
                {price && (
                  <Typography variant="h6" color="primary" fontWeight={700} sx={{ mt: 0.5 }}>
                    ${price}/{interval}
                  </Typography>
                )}
                {trialDays > 0 && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                    Includes {trialDays}-day free trial — you won't be charged today
                  </Typography>
                )}
              </Box>
            );
          })()}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>Card Number</Typography>
          <TextField
            fullWidth
            value={cardNumber}
            onChange={(e) => setCardNumber(e.target.value)}
            placeholder="4242 4242 4242 4242"
            sx={{ mb: 1.5 }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Test cards — click to use:
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            <Chip icon={<CheckCircleOutlineIcon />} label="Success" size="small" color="success" variant="outlined"
              onClick={() => setCardNumber('4242424242424242')} />
            <Chip icon={<ErrorOutlineIcon />} label="Declined" size="small" color="error" variant="outlined"
              onClick={() => setCardNumber('4000000000000002')} />
            <Chip icon={<ErrorOutlineIcon />} label="Insufficient funds" size="small" color="warning" variant="outlined"
              onClick={() => setCardNumber('4000000000009995')} />
            <Chip icon={<ErrorOutlineIcon />} label="Expired" size="small" color="warning" variant="outlined"
              onClick={() => setCardNumber('4000000000000069')} />
            <Chip icon={<ErrorOutlineIcon />} label="Bad CVC" size="small" color="warning" variant="outlined"
              onClick={() => setCardNumber('4000000000000127')} />
            <Chip icon={<ErrorOutlineIcon />} label="Processing error" size="small" color="warning" variant="outlined"
              onClick={() => setCardNumber('4000000000000119')} />
            <Chip icon={<ErrorOutlineIcon />} label="Lost card" size="small" color="error" variant="outlined"
              onClick={() => setCardNumber('4000000000009987')} />
            <Chip icon={<ErrorOutlineIcon />} label="Stolen card" size="small" color="error" variant="outlined"
              onClick={() => setCardNumber('4000000000009979')} />
            <Chip icon={<ErrorOutlineIcon />} label="Fraud" size="small" color="error" variant="outlined"
              onClick={() => setCardNumber('4100000000000019')} />
          </Stack>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField label="Exp" value="12/29" disabled size="small" sx={{ flex: 1 }} />
            <TextField label="CVC" value="123" disabled size="small" sx={{ flex: 1 }} />
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handlePay}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {processing ? 'Processing…' : isTrial ? 'Save Card & Start Trial' : 'Pay Now'}
          </Button>

          {session?.cancel_url && (
            <Button
              fullWidth
              sx={{ mt: 1 }}
              href={session.cancel_url}
            >
              Cancel
            </Button>
          )}
        </CardContent>
      </Card>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
        This is a mock payment page for development. No real payments are processed.
      </Typography>
    </Container>
  );
}
